import React, { useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";
import { useInput, useLoop, Joystick, Token, dist, clamp } from "./arena";

const rnd = (a, b) => a + Math.random() * (b - a);
const TARGET = 8;
const mkDef = () => Array.from({ length: 4 }).map((_, i) => ({ id: i, x: 18 + i * 21, y: rnd(14, 40), out: false }));

// Kabaddi: cross the line, tag defenders, and return to your half before your breath runs out.
export default function Kabaddi() {
  const [phase, setPhase] = useState("ready");
  const [breath, setBreath] = useState(100);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [tag, setTag] = useState(0);
  const [msg, setMsg] = useState("");
  const [, setTick] = useState(0);
  const player = useRef({ x: 50, y: 82 });
  const defs = useRef(mkDef());
  const wasOpp = useRef(false);
  const holdT = useRef(0);
  const { dir, setJoy } = useInput();

  const resetRaid = () => { player.current = { x: 50, y: 82 }; defs.current = mkDef(); wasOpp.current = false; holdT.current = 0; setBreath(100); setTag(0); };
  const start = () => { setScore(0); setLives(3); setMsg(""); resetRaid(); setPhase("play"); };
  const fail = (m) => { setLives((l) => { const nl = l - 1; if (nl <= 0) { setPhase("over"); } else { setMsg(m); resetRaid(); } return nl; }); };

  useLoop(phase === "play", (dt) => {
    const p = player.current, d = dir();
    p.x = clamp(p.x + d.x * 40 * dt, 4, 96);
    p.y = clamp(p.y + d.y * 40 * dt, 4, 96);
    const inOpp = p.y < 48;
    // breath
    setBreath((b) => {
      const nb = clamp(b + (inOpp ? -17 : 55) * dt, 0, 100);
      if (nb <= 0 && inOpp) fail("Saans khatam! Breathe next time.");
      return nb;
    });
    // defenders chase, but a touch slower so a nimble raider can dodge
    defs.current.forEach((f) => {
      if (f.out) return;
      if (inOpp) { const ax = p.x - f.x, ay = p.y - f.y, m = Math.hypot(ax, ay) || 1; f.x = clamp(f.x + (ax / m) * 17 * dt, 3, 97); f.y = clamp(f.y + (ay / m) * 17 * dt, 3, 46); }
      if (dist(p, f) < 5.4) { if (inOpp) { sfx.hit?.(); f.out = true; setTag((t) => t + 1); } }
    });
    // tackle only if 2+ live defenders keep you surrounded for a moment (grab meter) — no instant-out
    if (inOpp) {
      const near = defs.current.filter((f) => !f.out && dist(p, f) < 6).length;
      holdT.current = near >= 2 ? holdT.current + dt : Math.max(0, holdT.current - dt * 3);
      if (holdT.current > 0.85) fail("Tackle! Defenders held you down.");
    } else { holdT.current = 0; }
    // bank on return
    if (wasOpp.current && !inOpp) {
      setTag((t) => { if (t > 0) { setScore((s) => { const ns = s + t; if (ns >= TARGET) { setPhase("win"); sfx.win?.(); } return ns; }); setMsg(`Safe return! +${t}`); } defs.current = mkDef(); return 0; });
      setBreath(100);
    }
    wasOpp.current = inOpp;
    setTick((x) => (x + 1) % 1e6);
  });

  const inOpp = player.current.y < 48;
  return (
    <div className="w-full text-center">
      <div className="flex flex-wrap justify-center gap-2">
        <AScore label="Score" value={`${score}/${TARGET}`} />
        <AScore label="This raid" value={tag} />
        <AScore label="Lives" value={"❤".repeat(Math.max(0, lives)) || "—"} />
      </div>
      <p className="my-2 text-sm text-kirana-brown/70">Cross the line, tag defenders, and rush back to your half before your breath ends! (Chant kabaddi-kabaddi 😤)</p>
      <div data-testid="kb-arena" className="relative mx-auto mb-2 h-72 w-full max-w-md overflow-hidden rounded-md border-2 border-kirana-border bg-gradient-to-b from-[#e0b97e] to-[#c99a5b]">
        <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 bg-white" />
        <span className="absolute left-2 top-2 text-[10px] font-bold text-kirana-red">OPPONENT</span>
        <span className="absolute bottom-2 right-2 text-[10px] font-bold text-kirana-green">YOU {inOpp ? "· RAIDING" : "· SAFE"}</span>
        {phase !== "ready" && <Token testid="kb-raider" x={player.current.x} y={player.current.y} color="#1D6FB8" label="😤" size={32} z={8} ring={inOpp} />}
        {phase !== "ready" && defs.current.map((f) => <Token key={f.id} x={f.x} y={f.y} color={f.out ? "#9aa0a6" : "#8B1E3F"} label={f.out ? "😵" : "🧑"} dim={f.out} size={28} />)}
        {phase === "play" && <div className="absolute inset-x-3 bottom-2 h-2 overflow-hidden rounded-full bg-black/30"><div className="h-full bg-kirana-gold transition-all" style={{ width: `${breath}%` }} /></div>}
      </div>
      {msg && phase === "play" && <p className="mb-1 text-sm font-bold text-kirana-brown">{msg}</p>}
      {phase === "play" && <div className="flex items-center justify-center gap-4"><Joystick onMove={setJoy} /><span className="hidden text-xs text-kirana-brown/60 sm:inline">Move: WASD / Arrows</span></div>}
      <div className="mt-3">
        {phase === "ready" && <ABtn data-testid="kb-start" onClick={start} tone="gold" className="px-8 py-3">Start Raid</ABtn>}
        {(phase === "win" || phase === "over") && (<><AResult testId="kb-result" win={phase === "win"} text={phase === "win" ? "Raid master! You won! 🏆" : `Out! Final score ${score}.`} /><div className="mt-3 flex justify-center"><ABtn data-testid="kb-again" onClick={start} tone="green">Play Again</ABtn></div></>)}
      </div>
    </div>
  );
}
